import { match } from 'ts-pattern'
import type {
  BooleanQuestion,
  DropdownQuestion,
  LikertScaleQuestion,
  MultipleChoiceQuestion,
  NullableBooleanQuestion,
  RatingQuestion,
  SignupReasonQuestion,
  SurveyDescription,
  SurveyField,
  SurveyQuestion
} from '~/types/surveys'
import type { RadioOption } from '~/types/elementsUi'
import { z, type ZodType } from 'zod'
import statusMessages from '/@lang/status-messages.json'

export function surveyQuestionToValidation(question: SurveyQuestion) {
  const surveyStatusMessages =
    statusMessages['survey-status-messages'][useLocaleStore().locale]

  let validation: ZodType = match(question.type)
    .with('likert', () => z.number())
    .with('boolean', () => z.boolean())
    .with('nullable-boolean', () => z.boolean().or(z.string()))
    .with('dropdown', () => z.string().or(z.number()))
    .with('signup-reason-selector', () => z.number())
    .with('multiple-choice', () => z.string().array())
    .with('open', () => z.string())
    .with('boolean-open', () => z.string().nullable())
    .with('rating', () => {
      const ratingQuestion = question as RatingQuestion

      const message = surveyStatusMessages.rating
        .replace('${min}', ratingQuestion.minRating.toString())
        .replace('${max}', ratingQuestion.maxRating.toString())

      return z
        .number()
        .int()
        .min(ratingQuestion.minRating, {
          error: message
        })
        .max(ratingQuestion.maxRating, {
          error: message
        })
    })
    .with('description', () => z.never())
    .exhaustive()

  if (
    question.optional &&
    question.type !== 'nullable-boolean' &&
    question.type !== 'boolean-open'
  ) {
    validation = validation.optional()
  }

  if (question.visibleIf) {
    validation = validation.optional()
  }

  return markRaw(validation)
}

export function surveyQuestionToField(question: SurveyQuestion): SurveyField {
  return match(question.type)
    .returnType<SurveyField>()
    .with('likert', () => {
      return {
        key: question.key,
        type: 'radio',
        title: question.title,
        visibleIf: question.visibleIf,
        options: (question as LikertScaleQuestion).answers.map((answer) => {
          return {
            name: answer.answer,
            id: answer.score
          }
        })
      }
    })
    .with('boolean', () => {
      const booleanQuestion = question as BooleanQuestion
      const trueAnswer = {
        name: booleanQuestion.trueAnswer.answer,
        id: true
      } satisfies RadioOption
      const falseAnswer = {
        name: booleanQuestion.falseAnswer.answer,
        id: false
      } satisfies RadioOption
      return {
        key: booleanQuestion.key,
        type: 'radio',
        title: question.title,
        visibleIf: question.visibleIf,
        options: [trueAnswer, falseAnswer]
      }
    })
    .with('nullable-boolean', () => {
      const booleanQuestion = question as NullableBooleanQuestion
      const trueAnswer = {
        name: booleanQuestion.trueAnswer.answer,
        id: true
      } satisfies RadioOption
      const falseAnswer = {
        name: booleanQuestion.falseAnswer.answer,
        id: false
      } satisfies RadioOption
      const nullAnswer = {
        name: booleanQuestion.nullAnswer.answer,
        id: 'unknown'
      } satisfies RadioOption
      return {
        key: booleanQuestion.key,
        type: 'radio',
        title: question.title,
        visibleIf: question.visibleIf,
        options: [trueAnswer, falseAnswer, nullAnswer]
      }
    })
    .with('boolean-open', () => {
      const booleanQuestion = question as BooleanQuestion
      const trueAnswer = {
        name: booleanQuestion.trueAnswer.answer,
        id: true
      } satisfies RadioOption
      const falseAnswer = {
        name: booleanQuestion.falseAnswer.answer,
        id: false
      } satisfies RadioOption

      return {
        key: question.key,
        title: question.title,
        visibleIf: question.visibleIf,
        type: 'radio-open',
        options: [trueAnswer, falseAnswer]
      }
    })
    .with('dropdown', () => {
      const dropdownQuestion = question as DropdownQuestion
      return {
        key: dropdownQuestion.key,
        type: 'selector',
        title: question.title,
        visibleIf: question.visibleIf,
        options: dropdownQuestion.options
      }
    })
    .with('signup-reason-selector', () => {
      const signupReasonQuestion = question as SignupReasonQuestion
      return {
        key: signupReasonQuestion.key,
        type: 'signup-reason',
        title: question.title,
        visibleIf: question.visibleIf
      }
    })
    .with('multiple-choice', () => {
      const multipleChoiceQuestion = question as MultipleChoiceQuestion
      const options = multipleChoiceQuestion.options
      return {
        key: multipleChoiceQuestion.key,
        type: 'multiple-choice',
        title: question.title,
        visibleIf: question.visibleIf,
        options: options,
        allowOpen: multipleChoiceQuestion.allowOpen
      }
    })
    .with('rating', () => {
      const ratingsQuestion = question as RatingQuestion

      return {
        key: ratingsQuestion.key,
        type: 'rating',
        title: ratingsQuestion.title,
        visibleIf: ratingsQuestion.visibleIf,
        minRating: ratingsQuestion.minRating,
        maxRating: ratingsQuestion.maxRating
      }
    })
    .with('open', () => {
      return {
        key: question.key,
        title: question.title,
        visibleIf: question.visibleIf,
        type: 'textarea'
      }
    })
    .with('description', () => {
      const description = question as SurveyDescription

      return {
        key: description.key,
        title: description.title,
        type: 'description',
        description: description.description
      }
    })
    .exhaustive()
}
